/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   StatusMultiSelect.tsx
 *     Feature         :   254-pipeline-monitoring-overhaul (US3)
 *     Description     :   A compact multi-select status dropdown for the Job Activities filter
 *                         bar (FR-018/019). Shows each status with its facet count as a
 *                         checkbox list; the trigger summarises the current selection. Replaces
 *                         the loose chip row so status lives IN the filter section like every
 *                         other filter. Token-styled popover (SearchableSelect pattern).
 *     Constitution    :   Principle XLV (tokens + lucide)
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-24
 * =========================================================================================
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface StatusFacet { status: string; count: number }

export function StatusMultiSelect({
  facets, selected, onChange,
}: {
  facets: StatusFacet[];
  selected: string[];
  onChange: (next: string[]) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = (s: string): void => {
    const set = new Set(selected);
    if (set.has(s)) set.delete(s); else set.add(s);
    onChange([...set]);
  };

  const label = selected.length === 0
    ? 'All statuses'
    : selected.length === 1 ? selected[0] : `${selected.length} statuses`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 min-w-[10rem] items-center justify-between gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)]"
      >
        <span className="truncate">{label}</span>
        <ChevronDown size={14} className="flex-shrink-0 text-[var(--color-text-secondary)]" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 max-h-72 w-64 overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] py-1 shadow-lg">
          {facets.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">No statuses in range</div>
          ) : (
            <>
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="mb-1 block w-full border-b border-[var(--color-border)] px-3 py-1.5 text-left text-xs text-[var(--color-accent)] hover:bg-[var(--color-bg-subtle)]"
                >
                  Clear ({selected.length})
                </button>
              )}
              {facets.map((f) => {
                const on = selected.includes(f.status);
                return (
                  <button
                    key={f.status}
                    type="button"
                    onClick={() => toggle(f.status)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-[var(--color-bg-subtle)]"
                  >
                    <span className="flex items-center gap-2">
                      <span className={`flex h-4 w-4 items-center justify-center rounded border ${on ? 'border-[var(--color-accent)] bg-[var(--color-accent)]' : 'border-[var(--color-border)]'}`}>
                        {on && <Check size={11} className="text-white" />}
                      </span>
                      <span className="truncate text-[var(--color-text-primary)]">{f.status}</span>
                    </span>
                    <span className="flex-shrink-0 text-xs text-[var(--color-text-secondary)]">{f.count.toLocaleString()}</span>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
